import { type ReactNode, useEffect, useMemo, useRef } from 'react';

import classNames from 'classnames';

import type { DragAndDropHooks, IconName, Key, TreeNode } from '@iconik-io/uad';
import { ProgressCircle, SearchField, Text, Tree } from '@iconik-io/uad';

import { collectionIdFromTreeKey } from '../../../../utils/collection_tree_key';
import {
  INPUT_DEBOUNCE_TIMEOUT,
  useDebounce,
} from '../../../hooks/use_debounce';

import styles from './collections_panel.module.css';

type CollectionsPanelProps = {
  rootCollectionId: string;
  currentCollectionId?: string;
  treeNodes: TreeNode[];
  expandedKeys: Iterable<Key>;
  onExpandedChange: (keys: Set<Key>) => void;
  onLoadMore?: (parentId: string) => void;
  isLoading: boolean;
  scrollToSelected?: boolean;
  onAction?: (key: Key) => void;
  actionIcon?: IconName;
  onSelect?: (key: Key) => void;
  navigationTrigger?: 'row' | 'action-button';
  dragAndDropHooks?: DragAndDropHooks;
  isVirtualized?: boolean;
  disabledBehavior?: 'all' | 'selection';
  searchValue?: string;
  onSearchValueChange?: (value: string) => void;
  onSearch?: (value: string) => void;
  /** Rendered in the header row, after the search field. */
  actions?: ReactNode;
};

export function CollectionsPanel({
  rootCollectionId,
  currentCollectionId,
  treeNodes,
  expandedKeys,
  onExpandedChange,
  onLoadMore,
  isLoading,
  scrollToSelected = true,
  onAction,
  actionIcon = 'go_to',
  onSelect,
  navigationTrigger = 'row',
  dragAndDropHooks,
  isVirtualized,
  disabledBehavior,
  searchValue = '',
  onSearchValueChange,
  onSearch,
  actions,
}: CollectionsPanelProps) {
  const treeContainerRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = useDebounce(
    (value: string) => onSearch?.(value),
    INPUT_DEBOUNCE_TIMEOUT,
    [onSearch],
  );

  // Scroll selected item into view when currentCollectionId changes or scrollToSelected triggers
  useEffect(() => {
    if (
      isVirtualized ||
      !scrollToSelected ||
      !currentCollectionId ||
      !treeContainerRef.current
    ) {
      return;
    }

    // Small delay to ensure DOM has updated
    const timeoutId = setTimeout(() => {
      const selectedElement = treeContainerRef.current?.querySelector(
        '[aria-selected="true"]',
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'instant',
          block: 'nearest',
        });
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [currentCollectionId, scrollToSelected, isVirtualized]);

  // Tree keys are the path to a node, so the same collection can appear in more
  // than one place; every occurrence of the current one reads as selected.
  const selectedKeys = useMemo(() => {
    if (!currentCollectionId) {
      return [];
    }
    const keys: Key[] = [];
    const visit = (nodes: TreeNode[]): void => {
      for (const node of nodes) {
        if (collectionIdFromTreeKey(node.id) === currentCollectionId) {
          keys.push(node.id);
        }
        if (node.children) {
          visit(node.children);
        }
      }
    };
    visit(treeNodes);
    return keys;
  }, [treeNodes, currentCollectionId]);

  const isSearchActive =
    Boolean(onSearchValueChange) && searchValue.trim() !== '';
  const showNoResults = isSearchActive && !isLoading && treeNodes.length === 0;

  let content: ReactNode;
  if (isLoading && treeNodes.length === 0) {
    content = (
      <div className={styles.loading_container}>
        <ProgressCircle aria-label="Loading collections…" size="m" />
      </div>
    );
  } else if (showNoResults) {
    content = (
      <Text color="low_emphasis" className={styles.no_results}>
        No collections found
      </Text>
    );
  } else {
    content = (
      <div
        ref={treeContainerRef}
        className={classNames({
          [styles.container_virtualized]: isVirtualized,
        })}
      >
        <Tree
          items={treeNodes}
          defaultExpandedKeys={[rootCollectionId]}
          expandedKeys={expandedKeys}
          onExpandedChange={onExpandedChange}
          onLoadMore={onLoadMore}
          selectedKeys={selectedKeys}
          selectionMode="single"
          onSelectionChange={(keys) => {
            if (keys === 'all') {
              return;
            }

            if (keys.size > 0) {
              const [key] = keys;
              onSelect?.(key);
              if (navigationTrigger === 'row') {
                onAction?.(key);
              }
              const newExpandedKeys = new Set([...expandedKeys, ...keys]);
              onExpandedChange(newExpandedKeys);
            } else if (
              navigationTrigger === 'action-button' &&
              selectedKeys.length > 0
            ) {
              const newExpandedKeys = new Set(expandedKeys);
              const isExpanded = selectedKeys.some((key) =>
                newExpandedKeys.has(key),
              );
              for (const key of selectedKeys) {
                if (isExpanded) {
                  newExpandedKeys.delete(key);
                } else {
                  newExpandedKeys.add(key);
                }
              }
              onExpandedChange(newExpandedKeys);
            }
          }}
          onItemButtonPress={
            navigationTrigger === 'action-button' ? onAction : undefined
          }
          actionIcon={actionIcon}
          aria-label="Collections"
          dragAndDropHooks={dragAndDropHooks}
          isVirtualized={isVirtualized}
          disabledBehavior={disabledBehavior}
        />
      </div>
    );
  }

  if (!onSearchValueChange && !actions) {
    return content;
  }

  return (
    <div className={styles.container_with_search}>
      <div className={styles.search_row}>
        {onSearchValueChange ? (
          <SearchField
            aria-label="Search collections"
            placeholder="Search..."
            className={styles.search_field}
            value={searchValue}
            onChange={(value) => {
              onSearchValueChange(value);
              debouncedSearch(value);
            }}
            onClear={() => {
              // Reset the search immediately instead of waiting out the debounce.
              debouncedSearch.cancel();
              onSearchValueChange('');
              onSearch?.('');
            }}
            canClear
          />
        ) : null}
        {actions}
      </div>
      <div className={styles.tree_container}>{content}</div>
    </div>
  );
}
